<template> 
    <EditModal
        :open="open"
        :saveButtonLabel="$t('livechat.reassignChat.reassignBtn')"
        @save="onSave"
        @cancel="onCancel"
        maxWidth="800px"
        :loading="addLoading"
    >
        <template #title>
            {{ $t('livechat.reassignChat.availableAgents') }}
        </template>
        <template #subtitle>
            <v-text-field
                v-model="filter"
                append-icon="mdi-magnify"
                :label="$t('livechat.reassignChat.searchAgents')"
                single-line
                hide-details
            ></v-text-field>
        </template>
        
        <template #content>
            <v-data-table
                v-model="selected"
                :headers="headers"
                :items="availableAgents"
                :search="filter"
                item-key="email"
                show-select
                single-select
                :loading="loading"
            >
            <template v-slot:item.name="{ item }">
                <ProfileAvatar small :profilePicture="item.profilePicture" />
                <span class="ml-2 mr-2">{{ item.name }}</span>
                <StatusDot :active="item.status === 'online'" />
            </template>
            <template v-slot:item.groups="{ item }">
                <v-chip v-for="(group, index) in item.groups" :key="index">
                    {{ getGroupName(group) }} 
                </v-chip>
            </template>
            </v-data-table>
        </template>
    </EditModal>
</template>
<script>
import { mapGetters } from 'vuex';
import EditModal from '@/components/common/EditModal.vue';
import LivechatService from '@/services/livechat';
import ProfileAvatar from '@/components/common/ProfileAvatar.vue';
import StatusDot from '@/components/common/StatusDot.vue';

export default {
    name: 'LivechatReassignDialog',
    components: {
        EditModal,
        ProfileAvatar,
        StatusDot
    },
    props: {
        open: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            filter: '',
            availableAgents: [],
            selected: [],
            loading: false,
            addLoading: false,
            addingError: null,
        }
    },
    watch: {
        open(val) {
            if (val) {
                this.clearFields();
                this.loadAvailableAgents();
            }
        }
    },
    computed: {
        ...mapGetters('livechat', ['activeChat', 'organization', 'agent', 'agentStatus']),
        ...mapGetters('bots', ['currentBot']),
        ...mapGetters('auth', ['isSupervisor']),
        filteredFiles() {
            if (this.activeChatFiles?.length === 0) return [];

            return this.activeChatFiles?.filter(file => 
            file.name.toLowerCase().includes(this.filter.toLowerCase()) 
            || file.description.toLowerCase().includes(this.filter.toLowerCase())
            );
        },
        headers() {
            return [
                { text: this.$t('livechat.reassignChat.name'), value: 'name' },
                { text: this.$t('livechat.reassignChat.groups'), value: 'groups' }
            ];
        }
    },
    methods: {
        getGroupName(guuid) {
            return this.organization?.groups?.find(group => group.uuid === guuid)?.name || guuid;
        },
        async onSave() {
            if (this.selected.length === 0) return;
            try {
                this.addLoading = true;
                if (this.agentStatus === 'online') {
                    await this.sendMessageTransferMsg();
                }
                await LivechatService.reassignChat({
                    chatUuid: this.activeChat.uuid,
                    agentUuid: this.selected[0].uuid
                });

                this.$store.dispatch('livechat/cleanUpAfterReassign', { chatUuid: this.activeChat.uuid });

                if (this.agentStatus === 'offline' && this.isSupervisor) {
                    await this.$store.dispatch('livechat/loadChatsOrganization');
                }
                this.$emit('save');
                this.clearFields();
            } catch (error) {
                console.error(error);
                this.addingError = this.$t('livechat.reassignChat.reassignError');
            } finally {
                this.addLoading = false;
            }
        },
        onCancel() {
            this.$emit('cancel');
            this.clearFields();
        },
        clearFields() {
            this.selected = [];
            this.filter = '';
            this.addingError = null;
        },
        async loadAvailableAgents() {
            this.loading = true;
            try {
                const result = await LivechatService.isAvailable({
                    groups: this.organization.groups.map(group => group.uuid),
                    uniqueBotId: this.currentBot.uniqueBotId
                });
                this.availableAgents = result.agents.filter(agent => agent.uuid !== this.agent.uuid);
            } finally {
                this.loading = false;
            }
        },
        async sendMessageTransferMsg() {
            const data = {
                data: this.$t('livechat.reassignChat.transferringMessage'),
                type: 'message',
                agentUuid: this.agent.uuid,
                chatUuid: this.activeChat.uuid,
                sender: {
                role: 'agent',
                uniqueId: this.agent.uuid,
                },
                created_at: new Date() + '',
                updated_at: '',
            };
            try {
                await this.$store.dispatch('livechat/addMessage', data);
            } catch (err) {
                console.error('sending message failed', err);
            }
        },
    }
}
</script>